import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';

import type {
  ApiListingRequest,
  TablePageSelectorProps,
  TableWrapperRef,
} from '@bitstopco/bitstop-theme';
import { TablePageSelector, TableWrapper, authReducer, useModal } from '@bitstopco/bitstop-theme';
import { EMPTY_DATE_RANGE } from '@bitstopco/bitstop-theme/constants';

import omit from 'lodash/omit';
import { useParams } from 'react-router-dom';

import SalesStatsCards from '@/components/ui/SalesStatsCards';
import CardTable from '@/components/ui/card/CardTable';
import { appendToFilters } from '@/helpers/api';
import {
  useGetTransactionsExportMutation,
  useGetTransactionsQuery,
  useGetTransactionsStatsQuery,
} from '@/store/api/services/transactions/transactions';

import { getFilteredCustomerSalesConfig } from '@/helpers/roleBasedFiltering';
import { useDispenseAssets } from '@/hooks/useDispenseAssets';
import { useAppSelector } from '@/store';

import { DEFAULT_PAGINATION } from '@/constants/api';
import { MACHINE_SALES_STATS } from '@/constants/app/machine';

import type { Tag, TransactionsSale, WithTags } from '@/types';

const CustomerTransactions = () => {
  const { id = '0', saleId } = useParams();
  const { show } = useModal();
  const { assets } = useDispenseAssets();

  const [exportMutation] = useGetTransactionsExportMutation();

  const [payload, setPayload] = useState<ApiListingRequest>();

  const tableRef = useRef<TableWrapperRef>(null);

  // Get user role for column filtering
  const {
    selectors: { userInfo },
  } = authReducer;
  const userData = useAppSelector(userInfo);
  const userRoleName = userData?.role?.name;

  const computedPayload = useMemo(() => {
    if (!payload) {
      return skipToken;
    }

    const customerFilter = {
      operator: 'equal',
      property: 'customer_id',
      value: id,
    };

    return appendToFilters(payload, [customerFilter]);
  }, [payload, id]);

  const statsPayload = useMemo(() => {
    if (typeof computedPayload === 'symbol') {
      return computedPayload;
    }

    return omit(computedPayload, ['pagination', 'page', 'items_per_page']);
  }, [computedPayload]);

  const {
    data: {
      data: apiData = [],
      included,
      meta: { pagination: metaPagination = DEFAULT_PAGINATION } = {},
    } = {},
    isFetching,
  } = useGetTransactionsQuery(computedPayload);

  const {
    data: { data: stats } = {},
    isLoading: isLoadingTransactionsStats,
  } = useGetTransactionsStatsQuery(statsPayload);

  const data: WithTags<TransactionsSale>[] = useMemo(() => {
    const allTags = (included?.tags ?? []) as Tag[];
    return apiData.map((customer) => ({
      ...customer,
      tags: allTags.filter(({ id }) => customer.tag_ids.includes(id)),
    }));
  }, [apiData]);

  const { columns: filteredColumns, filters: filteredFilters } = useMemo(() => {
    const { columns, filters: filteredFilters } = getFilteredCustomerSalesConfig(userRoleName);
    const filters = filteredFilters.map((config) => {
      if (config.property === 'tag_ids') {
        return {
          ...config,
          options: [
            ...(included?.tags ?? []).map(({ id, name, color }) => ({
              label: name,
              value: id,
              color,
            })),
          ],
        };
      }
      if (config.property === 'coin_id') {
        return {
          ...config,
          options: [
            ...(included?.coins ?? []).map(({ id, ticker, is_token, chain }) => ({
              label: is_token ? `${ticker} / ${chain?.name}` : ticker,
              value: id,
            })),
          ],
        };
      }
      if (config.property === 'asset_id') {
        return {
          ...config,
          options: [...(assets ?? []).map(({ id, name }) => ({ label: name, value: id }))],
        };
      }
      return config;
    });
    return { columns, filters };
  }, [userData, included, assets]);

  useEffect(() => {
    if (saleId) {
      handleRow({ id: saleId } as TransactionsSale);
    }
  }, [saleId]);

  const handleRow = ({ id }: TransactionsSale) => {
    show('transaction-details', {
      data: {
        id,
        hideCustomerLink: true,
      },
    });
  };

  const handleExport = (type: string) => {
    if (!payload) {
      return;
    }
    // Use the getExportFile helper to export the Table data using the current payload
    tableRef.current?.getExportFile({
      type,
      mutation: exportMutation,
      overridePayload: computedPayload as ApiListingRequest,
      fileName: 'Customer-Sales-Report',
    });
  };

  // Custom render Page Selector, this is necessary since we are using a custom SalesStatsCards component
  const renderPageSelector = useCallback(
    (props: TablePageSelectorProps) => (
      <>
        <SalesStatsCards
          sx={{ mx: 3, mb: 2 }}
          stats={stats}
          mapData={MACHINE_SALES_STATS}
          isLoading={isLoadingTransactionsStats || !payload}
        />
        <TablePageSelector {...props} />
      </>
    ),
    [stats, payload, isLoadingTransactionsStats],
  );

  return (
    <CardTable sx={{ m: 0 }}>
      <TableWrapper<WithTags<TransactionsSale>>
        data={data}
        ref={tableRef}
        isLoading={isFetching || !payload}
        dateRangeFilterKey="created_at"
        sortByDefaultValue="created_at"
        columns={filteredColumns}
        filterConfigs={filteredFilters}
        dateRangeDefaultValue={EMPTY_DATE_RANGE}
        renderPageSelector={renderPageSelector}
        totalPages={metaPagination?.total_pages}
        totalItems={metaPagination?.total_items}
        hideActionMenu
        tableProps={{
          placeholder: {
            empty: {
              title: 'No sales found',
              content:
                "We can't find any sale that matches your search, please try a different request",
            },
          },
        }}
        tableToolbarProps={{
          searchPlaceholder: 'Search by Sale ID, TX Hash, or Wallet Address',
        }}
        onChange={setPayload}
        onRowClick={handleRow}
        onActionMenuItemClick={handleExport}
      />
    </CardTable>
  );
};

export default CustomerTransactions;
